import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Input, Select, SearchableSelect, Textarea, DatePicker, Btn, Modal, RNLLoader, BackButton } from '../UI';
import { fmt } from '../../utils';
import * as api from '../../api';
import {
  PAYMENT_TYPE_OPTIONS, CURRENCY_OPTIONS, PRIORITY_OPTIONS, parsePvErrorMessage,
  SUPPLIER_REQUIRED_PAYMENT_TYPES as SUPPLIER_REQUIRED_TYPES,
} from './pvUtils';
import VoucherOutcomeModal from './VoucherOutcomeModal';
import AdvancePaymentPanel from './AdvancePaymentPanel';
import AdvanceRecoveryPanel from './AdvanceRecoveryPanel';
import {
  allocate as allocateAdvance, advanceTotals, advanceIssues,
  PRO_RATA, INPUT_MODE_AMOUNT,
} from './advanceAllocation';

const money = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (n) => Number(n) || 0;
const isActive = (l) => l._selected !== false;

const lineAmount = (l) => money(num(l.process_quantity) * num(l.unit_rate));

// What a line contributes to the voucher. On an advance the goods have not
// moved, so the contribution is the advance allocated to that line rather
// than quantity x rate — this is the "Advance Base" that replaces the
// subtotal once the advance is applied. Mirrors the server, which recomputes
// the same thing and rejects a mismatch.
const lineMoney = (l, allocMap) => (allocMap ? money(allocMap[l._key] || 0) : lineAmount(l));

const computeTotals = (lines, ded, allocMap, recovered = 0) => {
  const active = lines.filter(isActive);
  const subtotal = money(active.reduce((sum, l) => sum + lineMoney(l, allocMap), 0));
  const vatableBase = money(active.filter(l => l.vat_applicable).reduce((sum, l) => sum + lineMoney(l, allocMap), 0));
  const whtableBase = money(active.filter(l => l.wht_applicable).reduce((sum, l) => sum + lineMoney(l, allocMap), 0));
  const gross = money(subtotal - num(ded.less_discount) - num(ded.less_retention) + num(ded.other_additions));
  const vat_amount = money(vatableBase * num(ded.vat_percent) / 100);
  const wht_amount = money(whtableBase * num(ded.wht_percent) / 100);
  const amount_payable = money(gross + vat_amount);
  // Advance recovery is a deduction like any other: the supplier already
  // holds that money, so it comes off what they are paid now.
  const net_payable = money(amount_payable - wht_amount - num(ded.withheld_vat) - num(ded.other_deductions) - money(recovered));
  return { subtotal, gross, vat_amount, wht_amount, amount_payable, net_payable, linesSelected: active.length };
};

const getIssues = (lines, ded, header, forSubmit, isAdvance) => {
  const issues = [];
  const active = lines.filter(isActive);
  active.forEach((l) => {
    const pq = num(l.process_quantity);
    const label = `${l.description || 'untitled'}`;
    // An advance pays ahead of delivery: quantity must be zero, not positive.
    if (isAdvance && pq !== 0) issues.push(`${label}: an advance carries no quantity — it must be 0.`);
    if (!isAdvance && pq <= 0) issues.push(`${label}: quantity must be greater than 0.`);
    if (!isAdvance && l.balance_quantity != null && pq > num(l.balance_quantity) + 0.001) {
      issues.push(`${label}: cannot process ${pq} — only ${l.balance_quantity} remain unprocessed on this line.`);
    }
    if (num(l.unit_rate) < 0) issues.push(`${label}: rate cannot be negative.`);
    if (!l.description) issues.push(`Line: description is required.`);
  });
  if (header.currency !== 'NGN' && !header.exchange_rate) {
    issues.push(`An exchange rate is required when currency is ${header.currency}.`);
  }
  if (forSubmit) {
    if (active.length === 0) issues.push('Select or add at least one line before submitting.');
    if (SUPPLIER_REQUIRED_TYPES.has(header.payment_type) && !header.supplier_id) {
      issues.push(`Payment type '${(PAYMENT_TYPE_OPTIONS.find(o => o.value === header.payment_type) || {}).label || header.payment_type}' requires a supplier to be selected.`);
    }
  }
  return issues;
};

const emptyHeader = {
  voucher_date: new Date().toISOString().slice(0, 10),
  payment_type: '', payment_method: '', priority: 'normal', manual_voucher_no: '',
  expense_category_id: '', narration: '', supplier_id: '', currency: 'NGN', exchange_rate: '',
  invoice_no: '', invoice_date: '', delivery_note_no: '', approval_memo_no: '',
  po_number: '', grn_number: '', pjv_number: '', jv_number: '', pi_number: '',
  purchase_request_id: '', requisition_no: '',
  // Section B. Pre-filled from the supplier master when one is picked, but
  // editable — a supplier whose record has no TIN or bank details would
  // otherwise leave the voucher office with nowhere to put what is on the
  // invoice in front of them. Saved against the voucher, never written back
  // to the supplier.
  supplier_tax_id_override: '', supplier_bank_name_override: '',
  supplier_account_name_override: '', supplier_account_number_override: '',
};
const emptyDeductions = { vat_percent: 7.5, wht_percent: 5, less_discount: 0, less_retention: 0, other_additions: 0, withheld_vat: 0, other_deductions: 0 };

const lineFromRequisitionLine = (l) => ({
  _key: `pr-${l.purchase_request_item_id}`,
  _selected: true,
  purchase_request_item_id: l.purchase_request_item_id,
  material_id: l.material_id, material_name: l.material_name,
  item_code: l.boq_item_code || null,
  description: l.description,
  specification: null,
  unit_of_measure: l.unit_of_measure,
  approved_quantity: l.approved_quantity,
  previously_paid_quantity: l.previously_paid_quantity,
  balance_quantity: l.balance_quantity,
  process_quantity: l.balance_quantity,
  unit_rate: l.approved_unit_rate,
  // Money axis, used only by the Advance panel. Quantity and money are
  // tracked separately: a delivery consumes quantity, an advance consumes
  // only value, and both draw on approved_value.
  approved_value: l.approved_value,
  processed_value: l.processed_value,
  previously_advanced_value: l.previously_advanced_value,
  eligible_advance_value: l.eligible_advance_value,
  vendor_id: l.vendor_id, vendor_name: l.vendor_name,
  boq_item_id: l.boq_item_id, boq_item_code: l.boq_item_code,
  boq_component_id: l.boq_component_id,
  account_ledger_id: l.account_ledger_id,
  expense_category_id: l.expense_category_id,
  cost_centre_id: l.cost_centre_id,
  project_id: l.project_id,
  discipline_id: null, phase: null,
  vat_applicable: true, wht_applicable: true, remarks: '',
});

const lineFromVoucherItem = (it) => ({
  _key: it.item_id,
  _selected: true,
  purchase_request_item_id: it.purchase_request_item_id,
  material_id: it.material_id, material_name: it.material_name,
  item_code: it.item_code,
  description: it.description,
  specification: it.specification,
  unit_of_measure: it.unit_of_measure,
  approved_quantity: it.approved_quantity,
  previously_paid_quantity: it.previously_paid_quantity,
  balance_quantity: it.balance_quantity,
  process_quantity: it.process_quantity,
  unit_rate: it.unit_rate,
  vendor_id: it.vendor_id, vendor_name: it.vendor_name,
  boq_item_id: it.boq_item_id, boq_component_id: it.boq_component_id,
  account_ledger_id: it.account_ledger_id,
  expense_category_id: it.expense_category_id,
  cost_centre_id: it.cost_centre_id,
  project_id: it.project_id,
  discipline_id: it.discipline_id, phase: it.phase,
  vat_applicable: it.vat_applicable, wht_applicable: it.wht_applicable, remarks: it.remarks || '',
});

const blankManualLine = () => ({
  _key: `manual-${Date.now()}-${Math.random()}`,
  _selected: true,
  purchase_request_item_id: null, material_id: '', material_name: '', item_code: '',
  description: '', specification: '', unit_of_measure: '',
  approved_quantity: null, previously_paid_quantity: 0, balance_quantity: null,
  process_quantity: '', unit_rate: '',
  vendor_id: '', vendor_name: '',
  boq_item_id: null, boq_component_id: null,
  account_ledger_id: '', expense_category_id: '', cost_centre_id: '', project_id: '',
  discipline_id: '', phase: '', vat_applicable: true, wht_applicable: true, remarks: '',
});

const NAVY = '#0b318b';

// Every control on this page has to be the same height. The shared Select is a
// styled <div>, not a native <select>, so the CSS rule in the page stylesheet
// can't reach it — it takes the identical metrics through its style prop.
const CTRL = { fontSize: 11, padding: '6px 9px', borderRadius: 7, minHeight: 32, boxSizing: 'border-box' };

// Read-only fields the system fills in itself. Tinted with the accent so it
// reads as a supplied value rather than something left blank. The shared Input
// spreads the style prop last, after its own disabled colour, so this wins.
const AUTO_FILLED = { color: 'var(--accent)', fontWeight: 700 };

export default function PaymentVoucherForm({
  initialRequisition, existingVoucher, voucherMethod, onBack, onSaved, toast,
  // Resubmission mode: the form supplies the fields, but the surrounding
  // screen owns the action. Its own footer and submit dialog are suppressed
  // and the live payload is handed upward instead.
  resubmitMode = false, onFormState,
}) {
  // Chosen in the method modal for a new voucher; read back off the record
  // when reopening an existing one so the title survives a reload.
  const methodName = voucherMethod?.name || existingVoucher?.voucher_method_name || null;
  const methodId = voucherMethod?.id || existingVoucher?.voucher_method_id || null;
  const [voucherId, setVoucherId] = useState(existingVoucher?.payment_voucher_id || null);
  const [voucherNo, setVoucherNo] = useState(existingVoucher?.voucher_no || null);
  const [statusLabel] = useState(existingVoucher ? existingVoucher.status.toUpperCase().replace(/_/g, ' ') : 'DRAFT');
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [header, setHeader] = useState(() => {
    if (existingVoucher) {
      return {
        voucher_date: existingVoucher.voucher_date || '',
        payment_type: existingVoucher.payment_type || '',
        payment_method: existingVoucher.payment_method || '',
        priority: existingVoucher.priority || 'normal',
        manual_voucher_no: existingVoucher.manual_voucher_no || '',
        expense_category_id: existingVoucher.expense_category_id || '',
        narration: existingVoucher.narration || '',
        supplier_id: existingVoucher.supplier_id || '',
        currency: existingVoucher.currency || 'NGN',
        exchange_rate: existingVoucher.exchange_rate || '',
        invoice_no: existingVoucher.invoice_no || '',
        invoice_date: existingVoucher.invoice_date || '',
        delivery_note_no: existingVoucher.delivery_note_no || '',
        approval_memo_no: existingVoucher.approval_memo_no || '',
        po_number: existingVoucher.po_number || '',
        grn_number: existingVoucher.grn_number || '',
        pjv_number: existingVoucher.pjv_number || '',
        jv_number: existingVoucher.jv_number || '',
        pi_number: existingVoucher.pi_number || '',
        purchase_request_id: existingVoucher.purchase_request_id || '',
        requisition_no: existingVoucher.purchase_request_pr_number || '',
        // The resolved values, so reopening a voucher shows what it actually
        // says rather than what the supplier master says today.
        supplier_tax_id_override: existingVoucher.supplier_tax_id || '',
        supplier_bank_name_override: existingVoucher.supplier_bank_name || '',
        supplier_account_name_override: existingVoucher.supplier_account_name || '',
        supplier_account_number_override: existingVoucher.supplier_bank_account || '',
      };
    }
    if (initialRequisition) {
      return { ...emptyHeader, purchase_request_id: initialRequisition.purchase_request_id, requisition_no: initialRequisition.pr_number };
    }
    return emptyHeader;
  });
  const [deductions, setDeductions] = useState(() => existingVoucher ? {
    vat_percent: existingVoucher.vat_percent, wht_percent: existingVoucher.wht_percent,
    less_discount: existingVoucher.less_discount, less_retention: existingVoucher.less_retention,
    other_additions: existingVoucher.other_additions, withheld_vat: existingVoucher.withheld_vat,
    other_deductions: existingVoucher.other_deductions,
  } : emptyDeductions);
  const [lines, setLines] = useState(() => {
    if (existingVoucher) return existingVoucher.items.map(lineFromVoucherItem);
    if (initialRequisition) return initialRequisition.lines.filter(l => !l.is_disabled).map(lineFromRequisitionLine);
    return [];
  });
  const disabledLines = useMemo(
    () => (initialRequisition ? initialRequisition.lines.filter(l => l.is_disabled) : []),
    [initialRequisition]
  );
  // Also true when REOPENING a voucher that was raised from a requisition —
  // initialRequisition is only set on the first load. Without the second test
  // an edited or resubmitted voucher lost its requisition framing: the SELECT
  // column vanished and an "Add Line" button appeared, offering to invent
  // lines the requisition never approved.
  const isFromRequisition = !!initialRequisition || !!existingVoucher?.purchase_request_id;

  const [masterData, setMasterData] = useState({ suppliers: [], materials: [], accountLedgers: [], expenseCategories: [], costCentres: [], projects: [], disciplines: [] });
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [backendError, setBackendError] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [approverId, setApproverId] = useState('');
  const [submitComment, setSubmitComment] = useState('');
  // Distinct from `submitting`: this covers fetching the approver list before
  // the dialog is shown, not the submit itself.
  const [preparingSubmit, setPreparingSubmit] = useState(false);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMaster(true);
      try {
        const [suppliers, materials, accountLedgers, expenseCategories, costCentres, projects, disciplines] = await Promise.all([
          api.listSuppliers(true), api.listMaterials(), api.listAccountLedgers(),
          api.listExpenseCategories(), api.listCostCentres(true), api.listProjects(), api.listDisciplines(),
        ]);
        if (!cancelled) {
          setMasterData({
            suppliers: suppliers || [], materials: materials || [], accountLedgers: accountLedgers || [],
            expenseCategories: expenseCategories || [], costCentres: costCentres?.cost_centres || costCentres || [],
            projects: (projects?.projects || projects || []), disciplines: disciplines || [],
          });
        }
      } catch (e) {
        if (!cancelled) toast.show(e.message, 'error');
      } finally {
        if (!cancelled) setLoadingMaster(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (existingVoucher) return; // already has its own voucher_no
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getNextVoucherNo();
        if (!cancelled) setVoucherNo(res.voucher_no);
      } catch (e) {
        if (!cancelled) toast.show(`Could not load the next voucher number: ${e.message}`, 'error');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [advance, setAdvanceRaw] = useState(() => ({
    enabled: existingVoucher?.is_advance || false,
    amount: existingVoucher?.advance_amount ?? '',
    percent: existingVoucher?.advance_percent ?? '',
    input_mode: existingVoucher?.advance_input_mode || INPUT_MODE_AMOUNT,
    allocation_basis: existingVoucher?.advance_allocation_basis || PRO_RATA,
    reason: existingVoucher?.advance_reason || '',
    recovery_method: existingVoucher?.advance_recovery_method || 'full_next_payment',
    supplier_advance_request_ref: existingVoucher?.supplier_advance_request_ref || '',
    advance_agreement_date: existingVoucher?.advance_agreement_date || '',
    expected_delivery_date: existingVoucher?.expected_delivery_date || '',
    advance_guarantee_reference: existingVoucher?.advance_guarantee_reference || '',
    performance_bond_reference: existingVoucher?.performance_bond_reference || '',
    advance_supporting_attachment: existingVoucher?.advance_supporting_attachment || '',
    manual_values: [],
    single_line_index: null,
  }));
  // A saved advance reopens already applied; any subsequent edit un-applies
  // it, so the summary can never show a figure the user has not confirmed.
  const [advanceApplied, setAdvanceApplied] = useState(!!existingVoucher?.is_advance);
  const setAdvance = (updater) => {
    setAdvanceRaw(updater);
    setAdvanceApplied(false);
  };

  const activeLines = useMemo(() => lines.filter(isActive), [lines]);
  const advTotals = useMemo(() => advanceTotals(activeLines), [activeLines]);
  const allocations = useMemo(() => {
    if (!advance.enabled) return [];
    return allocateAdvance(
      num(advance.amount),
      advance.allocation_basis,
      activeLines.map(l => num(l.eligible_advance_value)),
      { manualValues: advance.manual_values, singleLineIndex: advance.single_line_index },
    );
  }, [advance, activeLines]);

  const advIssues = useMemo(() => (advance.enabled ? advanceIssues({
    advanceAmount: num(advance.amount), reason: advance.reason,
    selectedLines: activeLines, allocations, remainingEligible: advTotals.remainingEligible,
  }) : []), [advance, activeLines, allocations, advTotals]);

  // Keyed by line so the summary keeps working if selection changes order.
  const allocMap = useMemo(() => {
    if (!advance.enabled || !advanceApplied) return null;
    return activeLines.reduce((acc, l, i) => ({ ...acc, [l._key]: allocations[i] || 0 }), {});
  }, [advance.enabled, advanceApplied, activeLines, allocations]);

  // ── Advance recovery ──────────────────────────────────────────
  // Only offered on an ordinary voucher: an advance cannot recover another
  // advance, which is exactly what the server refuses too.
  const outstandingAdvances = useMemo(
    () => (advance.enabled ? [] : (initialRequisition?.outstanding_advances || [])),
    [advance.enabled, initialRequisition],
  );
  const [recoveryAmounts, setRecoveryAmounts] = useState({});

  // Pre-fill from each advance's own recovery method the first time the
  // requisition's advances arrive. 'manual' deliberately starts blank — the
  // whole point of that method is that a human decides at recovery time.
  useEffect(() => {
    if (!outstandingAdvances.length) return;
    setRecoveryAmounts(prev => {
      const next = { ...prev };
      outstandingAdvances.forEach(a => {
        if (next[a.advance_voucher_id] !== undefined) return;
        next[a.advance_voucher_id] = a.recovery_method === 'full_next_payment'
          ? Number(a.outstanding_amount) || 0
          : '';
      });
      return next;
    });
  }, [outstandingAdvances]);

  const totalRecovered = useMemo(
    () => money(outstandingAdvances.reduce((sum, a) => sum + num(recoveryAmounts[a.advance_voucher_id]), 0)),
    [outstandingAdvances, recoveryAmounts],
  );

  const totals = useMemo(
    () => computeTotals(lines, deductions, allocMap, totalRecovered),
    [lines, deductions, allocMap, totalRecovered],
  );

  const recoveryIssues = useMemo(() => {
    const issues = [];
    outstandingAdvances.forEach(a => {
      const amt = num(recoveryAmounts[a.advance_voucher_id]);
      if (amt > num(a.outstanding_amount) + 0.01) {
        issues.push(`Advance ${a.voucher_no}: only ${fmt(a.outstanding_amount)} is still outstanding on it.`);
      }
    });
    if (totalRecovered > totals.amount_payable + 0.01) {
      issues.push(`Recovering ${fmt(totalRecovered)} exceeds the ${fmt(totals.amount_payable)} this voucher is paying — recover the remainder on a later voucher.`);
    }
    return issues;
  }, [outstandingAdvances, recoveryAmounts, totalRecovered, totals.amount_payable]);

  // Turning the advance on zeroes every quantity (money moves, goods do not);
  // turning it off restores each line's full remaining balance.
  const toggleAdvance = (on) => {
    setAdvanceRaw(prev => ({ ...prev, enabled: on }));
    setAdvanceApplied(false);
    setLines(prev => prev.map(l => ({
      ...l,
      process_quantity: on ? 0 : (l.balance_quantity ?? l.process_quantity),
    })));
  };
  const clientIssues = useMemo(
    () => [...getIssues(lines, deductions, header, false, advance.enabled), ...advIssues, ...recoveryIssues],
    [lines, deductions, header, advance.enabled, advIssues, recoveryIssues],
  );

  const updateHeader = (field, value) => setHeader(h => ({ ...h, [field]: value }));
  // Pull the picked supplier's details into Section B, leaving the fields
  // editable so a thin supplier record can be completed here.
  //
  // Keyed on the supplier CHANGING, via a ref rather than on every render of
  // the effect. Refilling unconditionally would wipe a reopened voucher's own
  // stored details on mount and replace them with whatever the supplier
  // master says today — which is the opposite of what a per-voucher override
  // is for.
  //
  // A genuine change does overwrite, including with blanks: carrying one
  // supplier's account number over to another is how a payment goes to the
  // wrong bank.
  const lastSupplierRef = useRef(header.supplier_id);
  useEffect(() => {
    if (lastSupplierRef.current === header.supplier_id) return;
    lastSupplierRef.current = header.supplier_id;
    const sup = masterData.suppliers.find(
      s => (s.id || s.supplier_id) === header.supplier_id);
    setHeader(h => ({
      ...h,
      supplier_tax_id_override: sup?.tax_id || '',
      supplier_bank_name_override: sup?.bank_name || '',
      supplier_account_number_override: sup?.bank_account || '',
      supplier_account_name_override: sup?.name || '',
    }));
  }, [header.supplier_id, masterData.suppliers]);
  const updateDeduction = (field, value) => setDeductions(d => ({ ...d, [field]: value }));
  const updateLine = (key, field, value) => setLines(ls => ls.map(l => l._key === key ? { ...l, [field]: value } : l));
  const removeLine = (key) => setLines(ls => ls.filter(l => l._key !== key));
  const addManualLine = () => setLines(ls => [...ls, blankManualLine()]);

  const buildPayload = () => {
    const allocFor = (l) => {
      if (!advance.enabled) return 0;
      const i = activeLines.findIndex(a => a._key === l._key);
      return i === -1 ? 0 : money(allocations[i] || 0);
    };
    return {
      voucher_method_id: methodId,
      voucher_date: header.voucher_date || null,
      payment_type: header.payment_type || null,
      payment_method: header.payment_method || null,
      priority: header.priority,
      manual_voucher_no: header.manual_voucher_no || null,
      expense_category_id: header.expense_category_id || null,
      narration: header.narration || null,
      supplier_id: header.supplier_id || null,
      supplier_tax_id_override: header.supplier_tax_id_override || null,
      supplier_bank_name_override: header.supplier_bank_name_override || null,
      supplier_account_name_override: header.supplier_account_name_override || null,
      supplier_account_number_override: header.supplier_account_number_override || null,
      currency: header.currency,
      exchange_rate: header.exchange_rate ? Number(header.exchange_rate) : null,
      invoice_no: header.invoice_no || null,
      invoice_date: header.invoice_date || null,
      delivery_note_no: header.delivery_note_no || null,
      approval_memo_no: header.approval_memo_no || null,
      po_number: header.po_number || null,
      grn_number: header.grn_number || null,
      pjv_number: header.pjv_number || null,
      jv_number: header.jv_number || null,
      pi_number: header.pi_number || null,
      purchase_request_id: header.purchase_request_id || null,
      is_advance: !!advance.enabled,
      advance_amount: advance.enabled ? num(advance.amount) : 0,
      advance_percent: advance.enabled && advance.percent !== '' ? num(advance.percent) : null,
      advance_input_mode: advance.enabled ? advance.input_mode : null,
      advance_allocation_basis: advance.enabled ? advance.allocation_basis : null,
      advance_reason: advance.enabled ? (advance.reason || null) : null,
      advance_recovery_method: advance.enabled ? (advance.recovery_method || null) : null,
      supplier_advance_request_ref: advance.enabled ? (advance.supplier_advance_request_ref || null) : null,
      advance_agreement_date: advance.enabled ? (advance.advance_agreement_date || null) : null,
      expected_delivery_date: advance.enabled ? (advance.expected_delivery_date || null) : null,
      advance_guarantee_reference: advance.enabled ? (advance.advance_guarantee_reference || null) : null,
      performance_bond_reference: advance.enabled ? (advance.performance_bond_reference || null) : null,
      advance_supporting_attachment: advance.enabled ? (advance.advance_supporting_attachment || null) : null,
      advance_recovered_amount: totalRecovered,
      advance_recoveries: outstandingAdvances
        .filter(a => num(recoveryAmounts[a.advance_voucher_id]) > 0)
        .map(a => ({
          advance_voucher_id: a.advance_voucher_id,
          // Attribute to the first requisition line on the voucher so per-line
          // headroom stays accurate; null would still be valid but coarser.
          purchase_request_item_id: (activeLines.find(l => l.purchase_request_item_id) || {}).purchase_request_item_id || null,
          amount: money(num(recoveryAmounts[a.advance_voucher_id])),
        })),
      items: activeLines.map(l => ({
        purchase_request_item_id: l.purchase_request_item_id || null,
        material_id: l.material_id || null,
        item_code: l.item_code || null,
        description: l.description,
        specification: l.specification || null,
        unit_of_measure: l.unit_of_measure || null,
        approved_quantity: l.approved_quantity ?? null,
        previously_paid_quantity: num(l.previously_paid_quantity),
        balance_quantity: l.balance_quantity ?? null,
        process_quantity: advance.enabled ? 0 : num(l.process_quantity),
        unit_rate: num(l.unit_rate),
        // On an advance the line's money is its allocation, not qty x rate —
        // the server recomputes exactly this and rejects any disagreement.
        line_amount: advance.enabled ? allocFor(l) : money(num(l.process_quantity) * num(l.unit_rate)),
        advance_allocated_amount: allocFor(l),
        vendor_id: l.vendor_id || null,
        boq_item_id: l.boq_item_id || null,
        boq_component_id: l.boq_component_id || null,
        account_ledger_id: l.account_ledger_id || null,
        expense_category_id: l.expense_category_id || null,
        cost_centre_id: l.cost_centre_id || null,
        project_id: l.project_id || null,
        discipline_id: l.discipline_id || null,
        phase: l.phase || null,
        vat_applicable: !!l.vat_applicable,
        wht_applicable: !!l.wht_applicable,
        remarks: l.remarks || null,
      })),
      subtotal_amount: totals.subtotal,
      vat_percent: num(deductions.vat_percent),
      wht_percent: num(deductions.wht_percent),
      less_discount: num(deductions.less_discount),
      less_retention: num(deductions.less_retention),
      other_additions: num(deductions.other_additions),
      gross_amount: totals.gross,
      vat_amount: totals.vat_amount,
      wht_amount: totals.wht_amount,
      withheld_vat: num(deductions.withheld_vat),
      other_deductions: num(deductions.other_deductions),
      amount_payable: totals.amount_payable,
      net_payable: totals.net_payable,
    };
  };

  // Kept in a ref by the parent, never in state — this fires on every edit and
  // storing it would loop.
  useEffect(() => {
    if (!onFormState) return;
    onFormState({ getPayload: buildPayload, issues: clientIssues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, deductions, header, advance, advanceApplied, totalRecovered, recoveryAmounts, clientIssues]);

  const handleSaveDraft = async () => {
    setBackendError(null);
    const issues = [...getIssues(lines, deductions, header, false, advance.enabled), ...advIssues, ...recoveryIssues];
    if (issues.length) { setBackendError({ header: `Fix ${issues.length} issue${issues.length === 1 ? '' : 's'} before saving:`, bullets: issues }); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      let savedNo = voucherNo;
      if (voucherId) {
        await api.updatePaymentVoucher(voucherId, payload);
      } else {
        const res = await api.createPaymentVoucher(payload);
        setVoucherId(res.payment_voucher_id);
        if (res.voucher_no) { setVoucherNo(res.voucher_no); savedNo = res.voucher_no; }
      }
      // A confirmation modal rather than a toast, and onSaved is deliberately
      // NOT called here — it unmounts this form immediately, which is why a
      // saved draft used to vanish with no visible confirmation at all. The
      // modal's onClose does the navigating.
      setOutcome({
        type: 'draft',
        title: 'Draft Saved',
        message: `Voucher ${savedNo || ''} has been saved as a draft. It has not been submitted for approval yet.`,
      });
    } catch (e) {
      setBackendError(parsePvErrorMessage(e.message));
    } finally {
      setSaving(false);
    }
  };

  const openSubmitModal = async () => {
    setBackendError(null);
    const issues = [...getIssues(lines, deductions, header, true, advance.enabled), ...advIssues, ...recoveryIssues];
    if (issues.length) { setBackendError({ header: `Fix ${issues.length} issue${issues.length === 1 ? '' : 's'} before submitting:`, bullets: issues }); return; }

    // Approvers are fetched BEFORE the modal opens, with the button showing
    // the wait. Opening first and loading inside meant a half-built dialog
    // where Confirm Submit was already clickable with no approver selected —
    // on a slow connection that is a submit with nothing chosen.
    setPreparingSubmit(true);
    try {
      const res = await api.getEligibleApprovers('PAYMENT_VOUCHER', { net_payable: totals.net_payable });
      const eligible = res?.eligible_users || [];
      setApprovers(eligible);
      if (eligible.length === 1) setApproverId(eligible[0].user_id || eligible[0].id);
      setShowSubmitModal(true);
    } catch (e) {
      toast.show(`Could not load approvers: ${e.message}`, 'error');
    } finally {
      setPreparingSubmit(false);
    }
  };

  const handleSubmit = async () => {
    if (!approverId) { toast.show('Select a Finance Head approver first.', 'error'); return; }
    setSubmitting(true);
    setBackendError(null);
    try {
      let id = voucherId;
      const payload = buildPayload();
      if (id) {
        await api.updatePaymentVoucher(id, payload);
      } else {
        const res = await api.createPaymentVoucher(payload);
        id = res.payment_voucher_id;
        setVoucherId(id);
        if (res.voucher_no) setVoucherNo(res.voucher_no);
      }
      await api.submitPaymentVoucher(id, { first_approver_id: approverId, comment: submitComment || null });
      setShowSubmitModal(false);
      setOutcome({
        type: 'submit',
        title: 'Voucher Submitted',
        message: `Voucher ${voucherNo || ''} has been submitted for approval.`,
      });
    } catch (e) {
      setShowSubmitModal(false);
      setBackendError(parsePvErrorMessage(e.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingMaster) {
    return <div style={{ padding: 60, textAlign: 'center' }}><RNLLoader title="Loading form data…" /></div>;
  }

  const supplierOptions = masterData.suppliers.map(s => ({ value: s.id || s.supplier_id, label: s.name }));
  const materialOptions = masterData.materials.map(m => ({ value: m.id || m.material_id, label: m.name }));
  const vendorOptions = supplierOptions;
  // These two APIs return entity-specific id fields (ledger_id,
  // expense_category_id), not a plain `id`. Reading `.id` gave every option
  // value `undefined` — which both tripped React's duplicate-key warning and,
  // worse, meant a chosen ledger or expense category never saved.
  const ledgerOptions = masterData.accountLedgers.map(l => ({ value: l.id || l.ledger_id, label: `${l.code} — ${l.name}` }));
  const expenseCategoryOptions = masterData.expenseCategories.map(c => ({ value: c.id || c.expense_category_id, label: c.name }));
  const costCentreOptions = masterData.costCentres.map(c => ({ value: c.id, label: c.name }));
  const projectOptions = masterData.projects.map(p => ({ value: p.id || p.project_id, label: p.name || p.project_name }));
  const disciplineOptions = masterData.disciplines.map(d => ({ value: d.id, label: d.name }));


  // The real column count: 19 headers, plus SELECT when a requisition is
  // loaded. It read 16 before CC GL was added — two short of the columns
  // that were already there, so the empty-state and fully-processed rows
  // stopped short of the table's right edge.
  const gridCols = 19 + (isFromRequisition ? 1 : 0);

  return (
    <div className="pv-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <BackButton onClick={onBack}>← Back to Payment Voucher Engine</BackButton>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {header.requisition_no && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}>
              From: <strong>{header.requisition_no}</strong>
              {initialRequisition && ` · ${initialRequisition.request_type_name || ''} · ${initialRequisition.requested_by_name || ''}`}
            </div>
          )}
          {methodName && (
            <span style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', padding: '4px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 700 }}>
              {methodName}
            </span>
          )}
        </div>
      </div>

      {backendError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 18px' }}>
          <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 13, marginBottom: backendError.bullets.length ? 8 : 0 }}>{backendError.header}</div>
          {backendError.bullets.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, color: '#991b1b', fontSize: 12.5, lineHeight: 1.7 }}>
              {backendError.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* ── Voucher header bar ── */}
      <style>{`
        .pv-top {
          background: ${NAVY};
          color: #fff; border-radius: 12px; padding: 14px 20px;
          display: flex; justify-content: space-between; align-items: center;
          gap: 20px; flex-wrap: wrap;
        }
        .pv-top-title { font-size: 19px; font-weight: 800; letter-spacing: .5px; }
        .pv-meta { display: flex; gap: 20px; align-items: flex-start; }
        .pv-meta-label { font-size: 9px; font-weight: 700; letter-spacing: .8px; opacity: .85; margin-bottom: 4px; }
        .pv-pill {
          background: #fff; color: ${NAVY}; border-radius: 8px;
          padding: 6px 14px; font-weight: 800; font-size: 12px; text-align: center; white-space: nowrap;
        }
        .pv-pill-status { background: rgba(255,255,255,0.22); color: #fff; }

        /* A + C down the left, B + D down the right — as per the design. */
        /* Columns stretch to the taller of the two and the LAST card in each grows
           into the slack, so C and D finish level at the bottom. */
        .pv-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; align-items: stretch; }
        .pv-col > :last-child { flex: 1; }
        .pv-col { display: flex; flex-direction: column; gap: 14px; }
        .pv-card {
          background: var(--bg-card); border: 1px solid black;
          border-radius: 10px; padding: 13px 14px;
        }
        .pv-sec { font-size: 10.5px; font-weight: 800; color: ${NAVY}; letter-spacing: .5px; margin-bottom: 10px; }

        /* Explicit column counts rather than auto-fit, so each section keeps
           the exact field arrangement the design specifies. */
        .pv-g5 { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 9px; }
        .pv-g3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 9px; }
        .pv-g2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px; }
        .pv-g5 + .pv-g3, .pv-g5 + .pv-g2, .pv-g3 + .pv-g2, .pv-g2 + .pv-g2, .pv-g2 + .pv-g1 { margin-top: 12px; }
        .pv-g1 { display: grid; grid-template-columns: 1fr; gap: 9px; }

        .pv-sumbar {
          background: ${NAVY}; color: #fff; border-radius: 10px; padding: 13px 22px;
          display: flex; justify-content: space-between; align-items: center;
          font-size: 11px; font-weight: 800; letter-spacing: .6px;
        }
        .pv-sum { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; align-items: stretch; }
        .pv-sumrow {
          display: flex; justify-content: space-between; align-items: center;
          padding: 6px 0; border-bottom: 1px dashed #e2e8f0; font-size: 11px;
        }
        .pv-money { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 12px; }
        .pv-money-label { font-size: 9.5px; font-weight: 800; color: #1d4ed8; letter-spacing: .3px; }
        .pv-money-sub { font-size: 9px; color: #64748b; margin: 2px 0 6px; }
        .pv-money-val { font-size: 18px; font-weight: 800; color: #1d4ed8; font-family: var(--mono); }

        /* The shared Input/Select/Textarea set their sizing inline, so a plain
           rule loses to them — hence !important, scoped to this page only. */
        .pv-page label { font-size: 11px !important; margin-bottom: 4px !important; }
        .pv-page input, .pv-page select, .pv-page textarea {
          font-size: 11px !important; padding: 6px 9px !important; border-radius: 7px !important;
          box-sizing: border-box !important;
        }
        /* Fixed height so inputs, date pickers and the div-based Select all
           line up on the same baseline. Textareas are excluded — they size to
           their rows. */
        .pv-page input { height: 32px !important; }
        .pv-page .pv-card { font-size: 11px; }

        @media (max-width: 1180px) {
          .pv-cols, .pv-sum { grid-template-columns: 1fr; }
          .pv-g5 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 640px) {
          .pv-g5, .pv-g3, .pv-g2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
      `}</style>

      <div className="pv-top">
        <div className="pv-top-title">
          PAYMENT VOUCHER{methodName ? ` — ${methodName.toUpperCase()}` : ''}
        </div>
        <div className="pv-meta">
          <div>
            <div className="pv-meta-label">VOUCHER NO.</div>
            <div className="pv-pill">{voucherNo || 'Loading…'}</div>
          </div>
          <div>
            <div className="pv-meta-label">STATUS</div>
            <div className="pv-pill pv-pill-status">{statusLabel}</div>
          </div>
        </div>
      </div>

      {/* ── A + C | B + D ── */}
      <div className="pv-cols">
        <div className="pv-col">
          <div className="pv-card">
            <div className="pv-sec">A — VOUCHER INFORMATION</div>
            <div className="pv-g5">
              <Input label="Voucher No." value={voucherNo || 'Loading…'} readOnly disabled style={AUTO_FILLED} />
              <DatePicker label="Voucher Date" value={header.voucher_date} onChange={v => updateHeader('voucher_date', v)} style={CTRL} />
              <Select label="Payment Type" value={header.payment_type} onChange={v => updateHeader('payment_type', v)} options={[{ value: '', label: '— Select —' }, ...PAYMENT_TYPE_OPTIONS]} style={CTRL} />
              <Input label="Payment Method" value={header.payment_method} onChange={v => updateHeader('payment_method', v)} placeholder="e.g. Bank Transfer" />
              <Input label="Status" value={statusLabel} readOnly disabled style={AUTO_FILLED} />
            </div>
            <div className="pv-g3">
              <Select label="Priority" value={header.priority} onChange={v => updateHeader('priority', v)} options={PRIORITY_OPTIONS} style={CTRL} />
              <Input label="Manual Voucher No." value={header.manual_voucher_no} onChange={v => updateHeader('manual_voucher_no', v)} placeholder="Optional legacy ref" />
              <Select label="Expense Category" value={header.expense_category_id} onChange={v => updateHeader('expense_category_id', v)} options={[{ value: '', label: '— None —' }, ...expenseCategoryOptions]} style={CTRL} />
            </div>
            <div style={{ marginTop: 12 }}>
              <Textarea label="Narration / Payment Description" value={header.narration} onChange={v => updateHeader('narration', v)} rows={3} placeholder="Narration / payment description" />
            </div>
          </div>

          <div className="pv-card">
            <div className="pv-sec">C — SUPPORTING DOCUMENTS</div>
            <div className="pv-g3">
              <Input label="Invoice No." value={header.invoice_no} onChange={v => updateHeader('invoice_no', v)} placeholder="Invoice number" />
              <DatePicker label="Invoice Date" value={header.invoice_date} onChange={v => updateHeader('invoice_date', v)} style={CTRL} />
              <Input label="Approval Memo No." value={header.approval_memo_no} onChange={v => updateHeader('approval_memo_no', v)} placeholder="Approval memo reference" />
            </div>
            <div className="pv-g2">
              <Input label="Requisition No." value={header.requisition_no || '—'} readOnly disabled style={AUTO_FILLED} />
              <Input label="Delivery Note No." value={header.delivery_note_no} onChange={v => updateHeader('delivery_note_no', v)} placeholder="Delivery note" />
            </div>
          </div>
        </div>

        <div className="pv-col">
          {(!isFromRequisition || initialRequisition?.require_vendor) && <div className="pv-card">
            <div className="pv-sec">B — SUPPLIER INFORMATION</div>
            <div className="pv-g5">
              <Select label="Supplier" value={header.supplier_id} onChange={v => updateHeader('supplier_id', v)} options={[{ value: '', label: '— None —' }, ...supplierOptions]} style={CTRL} />
              <Input label="Tax ID / TIN" value={header.supplier_tax_id_override}
                     onChange={v => updateHeader('supplier_tax_id_override', v)}
                     placeholder="Tax ID / TIN" style={AUTO_FILLED} />
              <Select label="Currency" value={header.currency} onChange={v => updateHeader('currency', v)} options={CURRENCY_OPTIONS} style={CTRL} />
              <Input label="Exchange Rate" type="number" value={header.exchange_rate} onChange={v => updateHeader('exchange_rate', v)} placeholder="1.00" />
              <Input label="Supplier Bank" value={header.supplier_bank_name_override}
                     onChange={v => updateHeader('supplier_bank_name_override', v)}
                     placeholder="Bank name" style={AUTO_FILLED} />
            </div>
            <div className="pv-g2">
              <Input label="Account Number" value={header.supplier_account_number_override}
                     onChange={v => updateHeader('supplier_account_number_override', v)}
                     placeholder="Account number" style={AUTO_FILLED} />
              <Input label="Account Name" value={header.supplier_account_name_override}
                     onChange={v => updateHeader('supplier_account_name_override', v)}
                     placeholder="Account name" style={AUTO_FILLED} />
            </div>
          </div>}

          <div className="pv-card">
            <div className="pv-sec">D — PROCUREMENT REFERENCES</div>
            <div className="pv-g2">
              <Input label="PO Number" value={header.po_number} onChange={v => updateHeader('po_number', v)} placeholder="Purchase Order" />
              <Input label="GRN Number" value={header.grn_number} onChange={v => updateHeader('grn_number', v)} placeholder="Goods Received Note" />
            </div>
            <div className="pv-g2">
              <Input label="PJV Number" value={header.pjv_number} onChange={v => updateHeader('pjv_number', v)} placeholder="Payment Journal Voucher" />
              <Input label="JV Number" value={header.jv_number} onChange={v => updateHeader('jv_number', v)} placeholder="Journal Voucher" />
            </div>
            <div className="pv-g1">
              <Input label="PI Number" value={header.pi_number} onChange={v => updateHeader('pi_number', v)} placeholder="Proforma Invoice No." />
            </div>
          </div>
        </div>
      </div>

      {/* ── Payment summary & deductions ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="pv-sumbar">
          <span>PAYMENT SUMMARY &amp; DEDUCTIONS</span>
          <span style={{ fontSize: 12, opacity: 0.9 }}>{voucherNo || ''}</span>
        </div>

        <div className="pv-sum">
          {/* ① */}
          <div className="pv-card">
            <div className="pv-sec">① VOUCHER SUMMARY &amp; TAXES</div>
            <div className="pv-sumrow"><span style={{ color: '#475569' }}>Lines Selected</span><strong>{totals.linesSelected}</strong></div>
            <div className="pv-sumrow"><span style={{ color: '#475569' }}>Subtotal</span><strong>{fmt(totals.subtotal)}</strong></div>

            <button
              onClick={() => setShowAdjustments(s => !s)}
              style={{ background: 'none', border: 'none', color: NAVY, fontWeight: 800, fontSize: 12, letterSpacing: 0.4, cursor: 'pointer', padding: '12px 0', textAlign: 'left' }}
            >
              {showAdjustments ? 'HIDE ADJUSTMENTS ▲' : 'SHOW ADJUSTMENTS ▼'}
            </button>
            {showAdjustments && (
              <div className="pv-g3" style={{ marginBottom: 14 }}>
                <Input label="Less Discount" type="number" value={deductions.less_discount} onChange={v => updateDeduction('less_discount', v)} />
                <Input label="Less Retention" type="number" value={deductions.less_retention} onChange={v => updateDeduction('less_retention', v)} />
                <Input label="Other Additions" type="number" value={deductions.other_additions} onChange={v => updateDeduction('other_additions', v)} />
              </div>
            )}

            <div className="pv-g2">
              <RateRow
                label="VAT" sub="(on Gross)" value={deductions.vat_percent}
                onChange={v => updateDeduction('vat_percent', v)}
                amount={`+${fmt(totals.vat_amount)}`} color="#16a34a"
              />
              <RateRow
                label="Withholding Tax" sub="(WHT)" value={deductions.wht_percent}
                onChange={v => updateDeduction('wht_percent', v)}
                amount={`−${fmt(totals.wht_amount)}`} color="#dc2626"
              />
            </div>

            <div className="pv-g2" style={{ marginTop: 12 }}>
              <Input label="Withheld VAT" type="number" value={deductions.withheld_vat} onChange={v => updateDeduction('withheld_vat', v)} placeholder="0.00" />
              <Input label="Other Deductions" type="number" value={deductions.other_deductions} onChange={v => updateDeduction('other_deductions', v)} placeholder="0.00" />
            </div>
          </div>

          {/* ② */}
          <div className="pv-card">
            <div className="pv-sec">② GROSS AMOUNT &amp; FINAL PAYMENT</div>
            <div className="pv-g2">
              <div className="pv-money">
                <div className="pv-money-label">GROSS AMOUNT</div>
                <div className="pv-money-sub">Subtotal − Discount − Retention + Additions</div>
                <div className="pv-money-val">{fmt(totals.gross)}</div>
              </div>
              <div className="pv-money">
                <div className="pv-money-label">AMOUNT PAYABLE</div>
                <div className="pv-money-sub">Gross + VAT</div>
                <div className="pv-money-val">{fmt(totals.amount_payable)}</div>
              </div>
            </div>
            {totalRecovered > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 12, padding: '9px 14px', background: '#fffdf5', border: '1px solid #fbbf24', borderRadius: 8 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#b45309', letterSpacing: 0.3 }}>LESS ADVANCE RECOVERED</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>Already paid to the supplier as an advance</div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#b45309', fontFamily: 'var(--mono)' }}>−{fmt(totalRecovered)}</div>
              </div>
            )}
            <div style={{ background: '#16a34a', borderRadius: 10, padding: 18, color: '#fff', marginTop: 12 }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.3 }}>NET PAYABLE</div>
              <div style={{ fontSize: 11, opacity: 0.9, margin: '3px 0 8px' }}>
                Gross + VAT − WHT − Withheld VAT − Other Deductions{totalRecovered > 0 ? ' − Advance Recovered' : ''}
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, fontFamily: 'var(--mono)' }}>{fmt(totals.net_payable)}</div>
            </div>
          </div>
        </div>
      </div>


      {/* ── Instructional banner ── */}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 18px', color: '#92400e', fontSize: 12.5 }}>
        {isFromRequisition
          ? 'Only lines with remaining balance shown. Enter quantity being processed now. System prevents over-processing.'
          : 'Add the line items this voucher is paying for using "Add Line" below.'}
      </div>

      {/* ── Line items ── */}
      <Card style={{ border: '1px solid black', padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: NAVY, letterSpacing: 0.5 }}>LINE ITEMS</div>
            {isFromRequisition && (
              <span style={{
                fontSize: 12,
                fontWeight: 700,
                color: initialRequisition?.require_vendor ? '#15803d' : '#b45309',
                background: initialRequisition?.require_vendor ? '#dcfce7' : '#fef3c7',
                border: `1px solid ${initialRequisition?.require_vendor ? '#86efac' : '#fcd34d'}`,
                borderRadius: 6,
                padding: '3px 10px',
                letterSpacing: 0.2,
              }}>
                Require Vendor: {initialRequisition?.require_vendor ? 'Yes' : 'No'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Advance is only offered against a loaded requisition — there is
                no approved value to measure it against otherwise, which is
                exactly what the server enforces. */}
            {isFromRequisition && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: NAVY }}>
                <input type="checkbox" checked={!!advance.enabled} onChange={e => toggleAdvance(e.target.checked)} />
                Advance Payment
              </label>
            )}
            {!isFromRequisition && <Btn size="sm" variant="primary" onClick={addManualLine}>Add Line</Btn>}
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: NAVY, color: '#fff' }}>
                {[
                  ...(isFromRequisition ? ['SELECT'] : []),
                  '#', 'ITEM', 'COST CONTROL GL', 'UNIT', 'APPR QTY', 'PREV PAID', 'BALANCE', 'PROCESS QTY', 'RATE', 'LINE AMT',
                  'VENDOR', 'COST CENTRE', 'EXPENSE', 'GL ACCOUNT', 'PROJECT', 'SUB CATEGORY', 'VAT', 'WHT', '',
                ].map((h, i) => (
                  <th key={`${h}-${i}`} style={{ padding: '9px 10px', textAlign: 'center', fontSize: 10.5, fontWeight: 800, whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.15)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {disabledLines.map((l) => (
                <tr key={`disabled-${l.purchase_request_item_id}`} style={{ background: '#fef2f2', opacity: 0.75 }}>
                  <td style={{ ...tdS, textAlign: 'center', color: '#b91c1c', fontWeight: 700 }}>✕</td>
                  <td style={{ ...tdS, textDecoration: 'line-through' }} colSpan={3}>{l.description}</td>
                  <td style={tdS}>{l.unit_of_measure}</td>
                  <td style={{ ...tdS }}>{l.approved_quantity}</td>
                  <td colSpan={gridCols - 5} style={{ ...tdS, color: '#b91c1c', fontStyle: 'italic' }}>{l.disabled_reason || 'Not available for processing'}</td>
                </tr>
              ))}
              {lines.map((l, idx) => {
                const active = isActive(l);
                return (
                  <tr key={l._key} style={{ borderBottom: '1px solid #e2e8f0', opacity: active ? 1 : 0.45, background: active ? 'transparent' : '#f8fafc' }}>
                    {isFromRequisition && (
                      <td style={{ ...tdS, textAlign: 'center' }}>
                        <input type="checkbox" checked={active} onChange={e => updateLine(l._key, '_selected', e.target.checked)} />
                      </td>
                    )}
                    <td style={{ ...tdS, textAlign: 'center', fontWeight: 700, color: '#475569' }}>{idx + 1}</td>
                    <td style={{ ...tdS, minWidth: 180 }}>
                      {l.purchase_request_item_id ? (
                        <span>{l.description}</span>
                      ) : (
                        <SearchableSelect
                          value={l.material_id}
                          onChange={v => {
                            const mat = masterData.materials.find(m => (m.id || m.material_id) === v);
                            updateLine(l._key, 'material_id', v);
                            if (mat) {
                              updateLine(l._key, 'description', mat.name);
                              updateLine(l._key, 'unit_of_measure', mat.unit_of_measure || l.unit_of_measure);
                            }
                          }}
                          options={materialOptions}
                          placeholder="Select material..."
                          style={{ minWidth: 170 }}
                          disabled={!active}
                        />
                      )}
                    </td>
                    {/* Read-only: the ledger belongs to the requisition line,
                        and the voucher reports it rather than setting it.
                        Blank for a line typed straight onto the voucher, which
                        has no requisition line behind it. */}
                    <td style={tdS}>{l.cost_control_general_ledger || '—'}</td>
                    <td style={tdS}>
                      {l.purchase_request_item_id ? l.unit_of_measure : (
                        <Input value={l.unit_of_measure} onChange={v => updateLine(l._key, 'unit_of_measure', v)} style={{ width: 70 }} disabled={!active} />
                      )}
                    </td>
                    <td style={{ ...tdS }}>{l.approved_quantity ?? '—'}</td>
                    <td style={{ ...tdS }}>{l.previously_paid_quantity ?? 0}</td>
                    <td style={{ ...tdS, fontWeight: 700, color: '#16a34a' }}>{l.balance_quantity ?? '—'}</td>
                    <td style={tdS}>
                      {/* An advance moves money, not goods — quantity is
                          locked at zero so the two axes can never be
                          consumed by the same voucher line. */}
                      <input type="number" value={advance.enabled ? 0 : l.process_quantity}
                        disabled={!active || advance.enabled}
                        title={advance.enabled ? 'An advance carries no quantity — it pays ahead of delivery' : undefined}
                        onChange={e => updateLine(l._key, 'process_quantity', e.target.value)}
                        style={{ ...inputS, width: 90, borderColor: advance.enabled ? '#cbd5e1' : '#2563eb' }} />
                    </td>
                    <td style={tdS}>
                      <input type="number" value={l.unit_rate} disabled={!active} onChange={e => updateLine(l._key, 'unit_rate', e.target.value)} style={{ ...inputS, width: 100 }} />
                    </td>
                    <td style={{ ...tdS, fontWeight: 700 }}>
                      {advance.enabled
                        ? <span style={{ color: 'var(--accent)' }}>{fmt(allocMap ? (allocMap[l._key] || 0) : 0)}</span>
                        : fmt(money(num(l.process_quantity) * num(l.unit_rate)))}
                    </td>
                    <td style={tdS}>
                      <SearchableSelect value={l.vendor_id} onChange={v => updateLine(l._key, 'vendor_id', v)} options={vendorOptions} placeholder="Vendor..." style={{ minWidth: 140, ...(isFromRequisition && !initialRequisition?.require_vendor ? { background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed' } : {}) }} disabled={!active || (isFromRequisition && !initialRequisition?.require_vendor)} />
                    </td>
                    <td style={tdS}>
                      <SearchableSelect value={l.cost_centre_id} onChange={v => updateLine(l._key, 'cost_centre_id', v)} options={costCentreOptions} placeholder="—" style={{ minWidth: 130 }} disabled={!active} />
                    </td>
                    <td style={tdS}>
                      <SearchableSelect value={l.expense_category_id} onChange={v => updateLine(l._key, 'expense_category_id', v)} options={expenseCategoryOptions} placeholder="—" style={{ minWidth: 140 }} disabled={!active} />
                    </td>
                    <td style={tdS}>
                      <SearchableSelect value={l.account_ledger_id} onChange={v => updateLine(l._key, 'account_ledger_id', v)} options={ledgerOptions} placeholder="—" style={{ minWidth: 150 }} disabled={!active} />
                    </td>
                    <td style={tdS}>
                      <SearchableSelect value={l.project_id} onChange={v => updateLine(l._key, 'project_id', v)} options={projectOptions} placeholder="—" style={{ minWidth: 140 }} disabled={!active} />
                    </td>
                    <td style={tdS}>
                      <SearchableSelect value={l.discipline_id} onChange={v => updateLine(l._key, 'discipline_id', v)} options={disciplineOptions} placeholder="—" style={{ minWidth: 130 }} disabled={!active} />
                    </td>
                    <td style={{ ...tdS, textAlign: 'center' }}>
                      <input type="checkbox" checked={l.vat_applicable} disabled={!active} onChange={e => updateLine(l._key, 'vat_applicable', e.target.checked)} />
                    </td>
                    <td style={{ ...tdS, textAlign: 'center' }}>
                      <input type="checkbox" checked={l.wht_applicable} disabled={!active} onChange={e => updateLine(l._key, 'wht_applicable', e.target.checked)} />
                    </td>
                    <td style={tdS}>
                      {!l.purchase_request_item_id && (
                        <button onClick={() => removeLine(l._key)} title="Remove line" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 15, padding: 4 }}>✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 && disabledLines.length === 0 && (
                <tr><td colSpan={gridCols} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No lines yet — click "Add Line" or load an approved requisition.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {outstandingAdvances.length > 0 && (
          <div style={{ padding: '14px 18px', borderTop: '1px solid #e2e8f0' }}>
            <AdvanceRecoveryPanel
              advances={outstandingAdvances}
              amounts={recoveryAmounts}
              setAmount={(id, v) => setRecoveryAmounts(prev => ({ ...prev, [id]: v }))}
              totalRecovered={totalRecovered}
              amountPayable={totals.amount_payable}
            />
          </div>
        )}

        {advance.enabled && (
          <div style={{ padding: '14px 18px', borderTop: '1px solid #e2e8f0' }}>
            <AdvancePaymentPanel
              advance={advance}
              setAdvance={setAdvance}
              selectedLines={activeLines}
              allocations={allocations}
              totals={advTotals}
              issues={advIssues}
              applied={advanceApplied}
              onApply={() => setAdvanceApplied(true)}
            />
          </div>
        )}
      </Card>


      {/* ── Issues + actions. These used to live inside the summary card; the
          summary now sits above the line items, and the submit buttons belong
          after the lines, not before them. ── */}
      <Card style={{ border: '1px solid black', padding: '16px 20px' }}>
        {clientIssues.length > 0 && (
          <div style={{ marginBottom: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: 12.5, marginBottom: 6 }}>⚠ {clientIssues.length} thing{clientIssues.length === 1 ? '' : 's'} to check:</div>
            <ul style={{ margin: 0, paddingLeft: 18, color: '#92400e', fontSize: 12, lineHeight: 1.7 }}>
              {clientIssues.map((iss, i) => <li key={i}>{iss}</li>)}
            </ul>
          </div>
        )}
        {!resubmitMode && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="ghost" onClick={onBack}>Cancel</Btn>
            <Btn variant="secondary" loading={saving} onClick={handleSaveDraft}>Save Draft</Btn>
            <Btn loading={preparingSubmit || submitting} onClick={openSubmitModal}>
              {preparingSubmit ? 'Preparing…' : 'Submit for Approval'}
            </Btn>
          </div>
        )}
      </Card>

      {showSubmitModal && (
        <Modal title="Submit Payment Voucher for Approval" onClose={() => setShowSubmitModal(false)} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              This voucher goes to the Finance Head first, then MD/DMD for final authorisation. Net payable: <strong>{fmt(totals.net_payable)}</strong>
            </div>
            {/* No loading branch: the dialog only opens once the approver list
                has arrived, so this select is always populated. */}
            {approvers.length === 0 ? (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                padding: '10px 12px', fontSize: 12, color: '#b91c1c', lineHeight: 1.5,
              }}>
                No Finance Head is available to approve this voucher. Someone must hold the
                <strong> finance_head</strong> role before it can be submitted.
              </div>
            ) : (
              <Select label="Finance Head Approver" value={approverId} onChange={setApproverId}
                options={[{ value: '', label: '— Select —' }, ...approvers.map(a => ({ value: a.user_id, label: a.name }))]} required style={CTRL} />
            )}
            <Textarea label="Comment (optional)" value={submitComment} onChange={setSubmitComment} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Btn variant="ghost" onClick={() => setShowSubmitModal(false)}>Cancel</Btn>
              <Btn loading={submitting} disabled={!approverId} onClick={handleSubmit}>Confirm Submit</Btn>
            </div>
          </div>
        </Modal>
      )}

      {outcome && <VoucherOutcomeModal outcome={outcome} onClose={() => { setOutcome(null); onSaved && onSaved(); }} />}
    </div>
  );
}

const tdS = {
  padding: '6px 8px', borderRight: '1px solid #e2e8f0', verticalAlign: 'middle',
  whiteSpace: 'nowrap', textAlign: 'center',
};
const inputS = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5 };

// A tax line as the design draws it: name on the left, a small editable
// percentage box, and the resulting amount on the right in its own colour.
function RateRow({ label, sub, value, onChange, amount, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <div style={{ lineHeight: 1.25 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#334155' }}>{label}</div>
        {sub && <div style={{ fontSize: 10.5, color: '#64748b' }}>{sub}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 56, padding: '5px 7px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5, textAlign: 'right', background: '#f8fafc' }}
        />
        <span style={{ fontSize: 11.5, color: '#64748b' }}>%</span>
      </div>
      <div style={{ fontWeight: 800, fontSize: 13, color, whiteSpace: 'nowrap' }}>{amount}</div>
    </div>
  );
}

